// The flat (2D) look of the cartoons: full-body characters drawn from the rig's pose, and the backgrounds they act in.
// A character is drawn with its feet at (0, 0), y growing downwards, in "body units" (an adult is about 6 units tall); the caller scales and places it.
import { KINDS, SETTINGS } from "./cast";
import { shade } from "../presenters";

const HIP = -2.55;
const SH_Y = -4.2;
const SH_X = 0.66;
const UARM = 1.0;
const FARM = 0.95;
const THIGH = 1.3;
const SHIN = 1.25;
const LEG_X = 0.3;
const OUTLINE = "rgba(30,20,20,0.55)";
const rad = (ctx, x, y, rx, ry = rx) => { ctx.beginPath(); ctx.ellipse(x, y, Math.max(0.001, rx), Math.max(0.001, ry), 0, 0, Math.PI * 2); };

function limb(ctx, x0, y0, a1, l1, a2, l2, w1, w2, c1, c2) {
  const x1 = x0 + Math.sin(a1) * l1;
  const y1 = y0 + Math.cos(a1) * l1;
  const x2 = x1 + Math.sin(a2) * l2;
  const y2 = y1 + Math.cos(a2) * l2;
  ctx.lineCap = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = w1 + 0.1;
  ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
  ctx.lineWidth = w2 + 0.1;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  ctx.strokeStyle = c1; ctx.lineWidth = w1;
  ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
  ctx.strokeStyle = c2; ctx.lineWidth = w2;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  return { x1, y1, x2, y2 };
}

// ------------------------------------------------------------------------------------------ faces
function eye(ctx, x, y, size, open, look, color = "#2b2118") {
  const h = Math.max(0.05, open) * size;
  rad(ctx, x, y, size * 0.95, h); ctx.fillStyle = "#fff"; ctx.fill();
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.05; ctx.stroke();
  if (open > 0.15) {
    ctx.save(); rad(ctx, x, y, size * 0.95, h); ctx.clip();
    rad(ctx, x + look * size * 0.3, y, size * 0.5, Math.min(size * 0.5, h)); ctx.fillStyle = color; ctx.fill();
    rad(ctx, x + look * size * 0.3 + size * 0.14, y - size * 0.15, size * 0.14); ctx.fillStyle = "#fff"; ctx.fill();
    ctx.restore();
  }
}

function brow(ctx, x, y, w, lift, side, color) {
  ctx.strokeStyle = color; ctx.lineCap = "round"; ctx.lineWidth = 0.13;
  const inner = -lift * 0.14 * side;                       // angry: the inner end lower; sad: higher
  ctx.beginPath();
  ctx.moveTo(x - w, y + (lift > 0 ? lift * 0.05 : 0) - inner * 0.0);
  ctx.quadraticCurveTo(x, y - 0.1 - lift * 0.12, x + w, y + inner * 1.2 - lift * 0.05);
  ctx.stroke();
}

// the mouth, from the voice (open, wide, round) and the mood (smile -1..1)
function mouth(ctx, x, y, size, m, smile, lip = "#8a3b3b", inside = "#3a0f18") {
  const open = Math.max(0, m.open);
  const half = size * (0.55 + 0.25 * (m.wide || 0) - 0.18 * (m.round || 0));
  ctx.lineCap = "round";
  if (open < 0.06) {
    ctx.strokeStyle = lip; ctx.lineWidth = 0.1;
    ctx.beginPath(); ctx.moveTo(x - half, y - smile * size * 0.1); ctx.quadraticCurveTo(x, y + smile * size * 0.5, x + half, y - smile * size * 0.1); ctx.stroke();
    return;
  }
  const h = size * (0.12 + open * 0.75);
  ctx.beginPath();
  ctx.moveTo(x - half, y);
  ctx.quadraticCurveTo(x, y - size * 0.08, x + half, y);
  ctx.quadraticCurveTo(x + half * 0.7, y + h * 1.25, x, y + h * 1.25);
  ctx.quadraticCurveTo(x - half * 0.7, y + h * 1.25, x - half, y);
  ctx.fillStyle = inside; ctx.fill();
  ctx.save(); ctx.clip();
  ctx.fillStyle = "#fbf9f4"; ctx.fillRect(x - half, y - 0.02, half * 2, Math.min(h * 0.4, size * 0.22));
  ctx.fillStyle = "#c75468"; rad(ctx, x, y + h * 1.1, half * 0.6, h * 0.4); ctx.fill();
  ctx.restore();
  ctx.strokeStyle = lip; ctx.lineWidth = 0.08;
  ctx.beginPath();
  ctx.moveTo(x - half, y);
  ctx.quadraticCurveTo(x, y - size * 0.08, x + half, y);
  ctx.quadraticCurveTo(x + half * 0.7, y + h * 1.25, x, y + h * 1.25);
  ctx.quadraticCurveTo(x - half * 0.7, y + h * 1.25, x - half, y);
  ctx.stroke();
}

function hairBack(ctx, ch, R, cy) {
  const c = ch.hair;
  ctx.fillStyle = c;
  if (ch.hairStyle === "long") { ctx.beginPath(); ctx.ellipse(0, cy + R * 0.55, R * 1.12, R * 1.55, 0, 0, Math.PI * 2); ctx.fill(); }
  if (ch.hairStyle === "bob") { ctx.beginPath(); ctx.ellipse(0, cy + R * 0.25, R * 1.18, R * 1.15, 0, 0, Math.PI * 2); ctx.fill(); }
  if (ch.hairStyle === "bun") { rad(ctx, 0, cy - R * 1.2, R * 0.38); ctx.fill(); }
  if (ch.hairStyle === "pigtails") { [-1, 1].forEach((sd) => { rad(ctx, sd * R * 1.15, cy + R * 0.35, R * 0.34, R * 0.7); ctx.fill(); }); }
}
function hairFront(ctx, ch, R, cy) {
  const c = ch.hair;
  const cap = (lift = 0.0, fringe = 0.34) => {
    ctx.beginPath();
    ctx.arc(0, cy, R * 1.03, Math.PI * 1.02, Math.PI * 1.98);
    ctx.quadraticCurveTo(R * 0.55, cy - R * (0.78 - fringe), 0, cy - R * (0.55 - lift));
    ctx.quadraticCurveTo(-R * 0.55, cy - R * (0.78 - fringe), -R * 1.03, cy - R * 0.03);
    ctx.fillStyle = c; ctx.fill();
  };
  switch (ch.hairStyle) {
    case "bald": ctx.fillStyle = shade(c, -0.1); [-1, 1].forEach((sd) => { rad(ctx, sd * R * 0.98, cy + R * 0.05, R * 0.12, R * 0.34); ctx.fill(); }); break;
    case "tuft": cap(0.05, 0.3); rad(ctx, R * 0.1, cy - R * 1.12, R * 0.16, R * 0.28); ctx.fillStyle = c; ctx.fill(); break;
    case "slick": cap(0.2, 0.2); break;
    case "side": cap(0.0, 0.5); break;
    case "pigtails": cap(0.0, 0.42); break;
    case "long": case "bob": cap(0.0, 0.46); break;
    case "bun": cap(0.1, 0.3); break;
    default: cap(0.0, 0.3);
  }
}

function humanHead(ctx, ch, st, R, cy, p, look) {
  const skin = ch.skin;
  hairBack(ctx, ch, R, cy);
  [-1, 1].forEach((sd) => { rad(ctx, sd * R * 0.97, cy + R * 0.1, R * 0.17, R * 0.24); ctx.fillStyle = shade(skin, -0.07); ctx.fill(); });
  rad(ctx, 0, cy, R, R * 1.02);
  const g = ctx.createRadialGradient(-R * 0.3, cy - R * 0.35, R * 0.1, 0, cy, R * 1.2);
  g.addColorStop(0, shade(skin, 0.1)); g.addColorStop(1, shade(skin, -0.08));
  ctx.fillStyle = g; ctx.fill();
  ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke();
  const tx = p.headTurn * R * 0.5;
  const ty = Math.max(0, p.headNod) * R * 0.22;
  const ex = R * 0.42;
  const es = R * 0.2;
  const open = Math.max(0.05, Math.min(1.4, p.eyes)) * (st.blink ? 1 - st.blink : 1);
  eye(ctx, -ex + tx, cy - R * 0.08 + ty, es, open, look, ch.eye || "#2b2118");
  eye(ctx, ex + tx, cy - R * 0.08 + ty, es, open, look, ch.eye || "#2b2118");
  brow(ctx, -ex + tx, cy - R * 0.42 + ty - p.brow * R * 0.06, es * 1.1, p.brow, 1, shade(ch.hair, -0.1));
  brow(ctx, ex + tx, cy - R * 0.42 + ty - p.brow * R * 0.06, es * 1.1, p.brow, -1, shade(ch.hair, -0.1));
  ctx.strokeStyle = shade(skin, -0.25); ctx.lineWidth = 0.07; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(tx - 0.03, cy + R * 0.08 + ty); ctx.quadraticCurveTo(tx + R * 0.12, cy + R * 0.25 + ty, tx - R * 0.02, cy + R * 0.28 + ty); ctx.stroke();
  const mouthM = { open: Math.max(p.mouth, st.open || 0), wide: st.wide, round: st.round };
  mouth(ctx, tx, cy + R * 0.55 + ty, R * 0.38, mouthM, p.smile, ch.lips || "#9a4141");
  [-1, 1].forEach((sd) => { const gr = ctx.createRadialGradient(sd * R * 0.62 + tx, cy + R * 0.3 + ty, 0, sd * R * 0.62 + tx, cy + R * 0.3 + ty, R * 0.26); gr.addColorStop(0, "rgba(230,90,90,0.28)"); gr.addColorStop(1, "rgba(230,90,90,0)"); ctx.fillStyle = gr; rad(ctx, sd * R * 0.62 + tx, cy + R * 0.3 + ty, R * 0.26); ctx.fill(); });
  if (ch.kind === "grandpa" || ch.kind === "grandma") {
    ctx.strokeStyle = "#3a3a44"; ctx.lineWidth = 0.07;
    [-1, 1].forEach((sd) => { rad(ctx, sd * ex + tx, cy - R * 0.08 + ty, es * 1.6); ctx.stroke(); });
    ctx.beginPath(); ctx.moveTo(-ex + es * 1.6 + tx, cy - R * 0.08 + ty); ctx.lineTo(ex - es * 1.6 + tx, cy - R * 0.08 + ty); ctx.stroke();
  }
  hairFront(ctx, ch, R, cy);
}

function animalHead(ctx, ch, st, R, cy, p, look) {
  const fur = ch.fur;
  const light = shade(fur, 0.35);
  const dark = shade(fur, -0.3);
  const kind = ch.kind;
  const tx = p.headTurn * R * 0.5;
  const ty = Math.max(0, p.headNod) * R * 0.2;
  const ear = (x, y, rx, ry, rot, inner) => { ctx.save(); ctx.translate(x, y); ctx.rotate(rot + p.ear * 0.3 * Math.sign(x || 1)); rad(ctx, 0, 0, rx, ry); ctx.fillStyle = fur; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke(); rad(ctx, 0, ry * 0.1, rx * 0.55, ry * 0.65); ctx.fillStyle = inner; ctx.fill(); ctx.restore(); };
  // things behind the head
  if (kind === "lion") { for (let i = 0; i < 14; i += 1) { const a = (i / 14) * Math.PI * 2; rad(ctx, Math.cos(a) * R * 1.05, cy + Math.sin(a) * R * 1.05, R * 0.46); ctx.fillStyle = i % 2 ? ch.accent : shade(ch.accent, 0.12); ctx.fill(); } }
  if (kind === "rabbit") { ear(-R * 0.45, cy - R * 1.55, R * 0.3, R * 0.95, -0.12, "#f2a8b8"); ear(R * 0.45, cy - R * 1.55, R * 0.3, R * 0.95, 0.12, "#f2a8b8"); }
  if (kind === "cat") { [-1, 1].forEach((sd) => { ctx.beginPath(); ctx.moveTo(sd * R * 0.9, cy - R * 0.3); ctx.lineTo(sd * R * 0.62, cy - R * 1.28); ctx.lineTo(sd * R * 0.12, cy - R * 0.85); ctx.closePath(); ctx.fillStyle = fur; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke(); ctx.beginPath(); ctx.moveTo(sd * R * 0.78, cy - R * 0.48); ctx.lineTo(sd * R * 0.62, cy - R * 1.05); ctx.lineTo(sd * R * 0.32, cy - R * 0.8); ctx.closePath(); ctx.fillStyle = "#f2a8b8"; ctx.fill(); }); }
  if (kind === "mouse") { ear(-R * 0.85, cy - R * 0.72, R * 0.52, R * 0.52, 0, "#f2a8b8"); ear(R * 0.85, cy - R * 0.72, R * 0.52, R * 0.52, 0, "#f2a8b8"); }
  if (kind === "bear") { ear(-R * 0.78, cy - R * 0.82, R * 0.34, R * 0.34, 0, light); ear(R * 0.78, cy - R * 0.82, R * 0.34, R * 0.34, 0, light); }
  if (kind === "lion") { ear(-R * 0.55, cy - R * 0.95, R * 0.28, R * 0.28, 0, light); ear(R * 0.55, cy - R * 0.95, R * 0.28, R * 0.28, 0, light); }
  if (kind === "dog") { [-1, 1].forEach((sd) => { ctx.save(); ctx.translate(sd * R * 0.88, cy - R * 0.45); ctx.rotate(sd * (0.35 + p.ear * 0.4)); rad(ctx, 0, R * 0.45, R * 0.3, R * 0.62); ctx.fillStyle = dark; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke(); ctx.restore(); }); }
  if (kind === "bird") { [-0.25, 0, 0.25].forEach((a, i) => { ctx.save(); ctx.translate(0, cy - R * 0.95); ctx.rotate(a); rad(ctx, 0, -R * 0.28, R * 0.12, R * 0.42 - i * 0.02); ctx.fillStyle = i === 1 ? shade(fur, 0.0) : dark; ctx.fill(); ctx.restore(); }); }
  // the head
  rad(ctx, 0, cy, R * (kind === "bird" ? 0.95 : 1.02), R * (kind === "rabbit" || kind === "mouse" ? 0.92 : 1));
  const g = ctx.createRadialGradient(-R * 0.3, cy - R * 0.35, R * 0.1, 0, cy, R * 1.2);
  g.addColorStop(0, shade(fur, 0.12)); g.addColorStop(1, shade(fur, -0.1));
  ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke();
  // muzzle
  const open = Math.max(p.mouth, st.open || 0);
  if (kind !== "bird") {
    const mw = kind === "mouse" ? 0.4 : kind === "cat" ? 0.5 : 0.56;
    rad(ctx, tx, cy + R * 0.42 + ty, R * mw, R * (kind === "dog" ? 0.42 : 0.36));
    ctx.fillStyle = kind === "mouse" ? shade(fur, 0.2) : light; ctx.fill();
    ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.05; ctx.stroke();
  }
  // eyes
  const es = R * (kind === "bird" ? 0.24 : 0.22);
  const exs = R * 0.42;
  const openE = Math.max(0.05, Math.min(1.4, p.eyes)) * (st.blink ? 1 - st.blink : 1);
  eye(ctx, -exs + tx, cy - R * 0.1 + ty, es, openE, look, "#1d1a17");
  eye(ctx, exs + tx, cy - R * 0.1 + ty, es, openE, look, "#1d1a17");
  brow(ctx, -exs + tx, cy - R * 0.42 + ty - p.brow * R * 0.05, es, p.brow, 1, dark);
  brow(ctx, exs + tx, cy - R * 0.42 + ty - p.brow * R * 0.05, es, p.brow, -1, dark);
  // nose, beak, whiskers and the mouth
  if (kind === "bird") {
    const by = cy + R * 0.28 + ty;
    const lower = open * R * 0.36;
    ctx.fillStyle = "#f08a2c"; ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.06;
    ctx.beginPath(); ctx.moveTo(-R * 0.3 + tx, by); ctx.quadraticCurveTo(tx, by - R * 0.34, R * 0.3 + tx, by); ctx.lineTo(tx, by + R * 0.12); ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.fillStyle = shade("#f08a2c", -0.15);
    ctx.beginPath(); ctx.moveTo(-R * 0.26 + tx, by + R * 0.04 + lower * 0.2); ctx.lineTo(R * 0.26 + tx, by + R * 0.04 + lower * 0.2); ctx.lineTo(tx, by + R * 0.3 + lower); ctx.closePath(); ctx.fill(); ctx.stroke();
  } else {
    ctx.fillStyle = kind === "cat" || kind === "rabbit" ? "#f08aa2" : "#2a2224";
    rad(ctx, tx, cy + R * 0.24 + ty, R * 0.14, R * 0.1); ctx.fill();
    mouth(ctx, tx, cy + R * 0.55 + ty, R * 0.32, { open, wide: st.wide, round: st.round }, p.smile, "#4a2a2a");
    if (kind === "cat" || kind === "mouse") { ctx.strokeStyle = "rgba(255,255,255,0.8)"; ctx.lineWidth = 0.035; [-1, 1].forEach((sd) => [-0.08, 0.06].forEach((dy) => { ctx.beginPath(); ctx.moveTo(sd * R * 0.35 + tx, cy + R * 0.42 + ty + dy * R); ctx.lineTo(sd * R * 1.05 + tx, cy + R * 0.38 + ty + dy * R * 3); ctx.stroke(); })); }
    if (kind === "rabbit") { ctx.fillStyle = "#fff"; ctx.fillRect(tx - R * 0.1, cy + R * 0.5 + ty, R * 0.2, R * 0.17); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.03; ctx.strokeRect(tx - R * 0.1, cy + R * 0.5 + ty, R * 0.2, R * 0.17); }
    if (kind === "dog" && open > 0.3) { ctx.fillStyle = "#e2778f"; rad(ctx, tx, cy + R * 0.9 + ty, R * 0.16, R * 0.2); ctx.fill(); }
  }
}

// ------------------------------------------------------------------------------------------ the body
const LEGS = { man: "trousers", grandpa: "trousers", boy: "shorts", woman: "skirt", girl: "skirt", grandma: "skirt" };

// Draws one character with its feet at the origin, facing right (the caller flips it). `st` = { blink, open, wide, round, name }, `p` = the pose from rig.poseAt.
export function drawCharacter(ctx, ch, st, p, t = 0) {
  const info = KINDS[ch.kind] || KINDS.man;
  const animal = info.species === "animal";
  const R = 0.82 * info.head;
  const skin = animal ? ch.fur : ch.skin;
  const top = ch.top;
  const bottom = ch.bottom;
  const lift = p.bob + p.jump;
  const hipY = HIP - lift;
  const bodyShade = shade(top, -0.18);
  const sitting = p.legL.h > 1.2;

  // the shadow on the ground
  ctx.save();
  rad(ctx, 0, 0.06, 1.0 - Math.min(0.5, p.jump * 0.3), 0.22); ctx.fillStyle = "rgba(0,0,0,0.22)"; ctx.fill();
  ctx.restore();

  // the tail
  if (animal && ch.kind !== "bird") {
    ctx.save(); ctx.translate(-0.45, hipY - 0.2); ctx.rotate(-0.5 + p.tail * 0.6);
    ctx.strokeStyle = OUTLINE; ctx.lineCap = "round";
    const len = ch.kind === "mouse" ? 1.7 : ch.kind === "rabbit" ? 0.3 : ch.kind === "bear" ? 0.35 : 1.5;
    ctx.lineWidth = (ch.kind === "mouse" ? 0.14 : ch.kind === "rabbit" || ch.kind === "bear" ? 0.0 : 0.36) + 0.1;
    if (ch.kind === "rabbit" || ch.kind === "bear") { rad(ctx, -0.15, 0, 0.3); ctx.fillStyle = ch.kind === "rabbit" ? "#fff" : shade(skin, -0.1); ctx.fill(); }
    else { ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(-len * 0.6, -0.4, -len, -len * 0.5 + p.tail * 0.5); ctx.stroke(); ctx.strokeStyle = ch.kind === "mouse" ? "#f2a8b8" : skin; ctx.lineWidth -= 0.1; ctx.stroke(); if (ch.kind === "lion") { rad(ctx, -len, -len * 0.5 + p.tail * 0.5, 0.28); ctx.fillStyle = ch.accent; ctx.fill(); } }
    ctx.restore();
  }
  if (ch.kind === "bird") { ctx.save(); ctx.translate(-0.5, hipY - 0.1); [-0.5, -0.2, 0.1].forEach((a, i) => { ctx.save(); ctx.rotate(a + p.tail * 0.2); rad(ctx, -0.5, 0, 0.55, 0.16); ctx.fillStyle = i === 1 ? skin : shade(skin, -0.2); ctx.fill(); ctx.restore(); }); ctx.restore(); }

  // the arm that is behind (left)
  const arm = (side, a) => {
    const sx = side * SH_X * (animal ? 0.9 : 1);
    const sy = SH_Y - lift;
    const sleeve = ch.kind === "bird" ? shade(skin, -0.08) : animal ? top : top;
    const handColor = animal ? shade(skin, 0.15) : skin;
    const pts = limb(ctx, sx, sy, a.s * (side < 0 ? 1 : 1), UARM, a.s + a.e, FARM, 0.46, 0.38, animal && ch.kind !== "bird" ? skin : sleeve, animal ? skin : skin);
    if (ch.kind === "bird") { rad(ctx, (pts.x1 + sx) / 2, (pts.y1 + sy) / 2, 0.22, 0.7); }
    rad(ctx, pts.x2, pts.y2, 0.22); ctx.fillStyle = handColor; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.05; ctx.stroke();
  };
  ctx.save();
  ctx.translate(0, hipY); ctx.rotate(p.lean + (p.shake || 0)); ctx.translate(0, -hipY);

  arm(-1, p.armL);
  // the legs
  const leg = (side, l) => {
    const hx = side * LEG_X;
    const kind = LEGS[ch.kind] || "fur";
    const thigh = kind === "trousers" ? bottom : kind === "shorts" ? bottom : skin;
    const shin = kind === "trousers" ? bottom : skin;
    const pts = limb(ctx, hx, hipY, l.h, THIGH, l.h - l.k, SHIN, 0.5, 0.44, thigh, shin);
    const footDir = sitting ? 1 : 1;
    ctx.save(); ctx.translate(pts.x2, pts.y2);
    rad(ctx, 0.18 * footDir, 0.05, 0.42, 0.22); ctx.fillStyle = animal ? shade(skin, 0.1) : ch.shoes || "#3a2f2a"; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.05; ctx.stroke();
    ctx.restore();
  };
  leg(-1, p.legL);
  leg(1, p.legR);

  // the torso (and the dress)
  ctx.beginPath();
  ctx.moveTo(-0.62, SH_Y - 0.15 - lift);
  ctx.quadraticCurveTo(0, SH_Y - 0.35 - lift, 0.62, SH_Y - 0.15 - lift);
  ctx.lineTo(0.55, hipY + 0.1);
  ctx.quadraticCurveTo(0, hipY + 0.28, -0.55, hipY + 0.1);
  ctx.closePath();
  const bg = ctx.createLinearGradient(-0.6, 0, 0.6, 0);
  bg.addColorStop(0, shade(top, 0.08)); bg.addColorStop(1, bodyShade);
  ctx.fillStyle = bg; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke();
  if (animal) { rad(ctx, 0.05, (SH_Y + hipY) / 2 - lift / 2 + 0.1, 0.34, 0.75); ctx.fillStyle = shade(skin, 0.3); ctx.globalAlpha = 0.9; ctx.fill(); ctx.globalAlpha = 1; ctx.fillStyle = shade(top, 0.05); ctx.fillRect(-0.55, hipY - 0.35, 1.1, 0.14); }
  if (LEGS[ch.kind] === "skirt") {
    ctx.beginPath();
    ctx.moveTo(-0.58, hipY - 0.55); ctx.lineTo(0.58, hipY - 0.55); ctx.lineTo(0.95, hipY + 1.1); ctx.quadraticCurveTo(0, hipY + 1.35, -0.95, hipY + 1.1); ctx.closePath();
    ctx.fillStyle = ch.kind === "grandma" ? shade(bottom, 0.15) : top; ctx.fill(); ctx.strokeStyle = OUTLINE; ctx.lineWidth = 0.07; ctx.stroke();
  } else if (!animal) {
    ctx.fillStyle = shade(bottom, -0.1); ctx.fillRect(-0.55, hipY - 0.25, 1.1, 0.34);
  }
  // neck, then the head
  const headY = SH_Y - 0.18 - R * 0.95 - lift;
  if (!animal || ch.kind === "bird") { ctx.fillStyle = skin; ctx.fillRect(-0.18, SH_Y - 0.5 - lift, 0.36, 0.42); }
  ctx.save();
  ctx.translate(0, SH_Y - 0.25 - lift); ctx.rotate(p.headTilt + Math.max(0, p.headNod) * 0.12); ctx.translate(0, -(SH_Y - 0.25 - lift));
  const look = st.look || 0;
  (animal ? animalHead : humanHead)(ctx, ch, st, R, headY, p, look);
  ctx.restore();
  arm(1, p.armR);
  ctx.restore();
}

// ------------------------------------------------------------------------------------------ the backgrounds
const cloud = (ctx, x, y, s) => { ctx.fillStyle = "rgba(255,255,255,0.92)"; [[0, 0, 1], [0.9, 0.15, 0.8], [-0.9, 0.2, 0.75], [0.3, -0.35, 0.8]].forEach(([dx, dy, k]) => { rad(ctx, x + dx * s, y + dy * s, s * 0.7 * k, s * 0.5 * k); ctx.fill(); }); };
const tree = (ctx, x, y, s, leaf = "#3f9e4b") => { ctx.fillStyle = "#7a5230"; ctx.fillRect(x - s * 0.09, y - s * 0.55, s * 0.18, s * 0.6); [[0, -0.95, 0.5], [-0.28, -0.7, 0.38], [0.28, -0.7, 0.38]].forEach(([dx, dy, k]) => { rad(ctx, x + dx * s, y + dy * s, s * k); ctx.fillStyle = leaf; ctx.fill(); }); };
const house = (ctx, x, y, w, color, roof) => { ctx.fillStyle = color; ctx.fillRect(x - w / 2, y - w * 0.6, w, w * 0.6); ctx.fillStyle = roof; ctx.beginPath(); ctx.moveTo(x - w * 0.62, y - w * 0.6); ctx.lineTo(x, y - w * 1.05); ctx.lineTo(x + w * 0.62, y - w * 0.6); ctx.closePath(); ctx.fill(); ctx.fillStyle = "#f6e7a8"; ctx.fillRect(x - w * 0.32, y - w * 0.45, w * 0.2, w * 0.2); ctx.fillStyle = "#6b4a2d"; ctx.fillRect(x + w * 0.1, y - w * 0.4, w * 0.2, w * 0.4); };

// Draws a setting into the whole frame. `horizon` (0..1) is where the ground starts; time drifts the clouds.
export function drawBackground(ctx, W, H, settingId, time = 0, horizon = 0.78) {
  const s = SETTINGS[settingId] || SETTINGS.park;
  const gy = H * horizon;
  const g = ctx.createLinearGradient(0, 0, 0, gy);
  g.addColorStop(0, s.sky[0]); g.addColorStop(1, s.sky[1]);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  const u = H / 10;
  if (s.indoor) {
    ctx.fillStyle = s.sky[0]; ctx.fillRect(0, 0, W, gy);
    ctx.fillStyle = shade(s.sky[0], -0.06); ctx.fillRect(0, gy - u * 0.25, W, u * 0.25);
    const fl = ctx.createLinearGradient(0, gy, 0, H); fl.addColorStop(0, s.ground); fl.addColorStop(1, shade(s.ground, -0.25)); ctx.fillStyle = fl; ctx.fillRect(0, gy, W, H - gy);
    if (settingId === "home") {
      ctx.fillStyle = "#9fd4f2"; ctx.fillRect(W * 0.1, H * 0.12, W * 0.18, H * 0.34); ctx.strokeStyle = "#fff"; ctx.lineWidth = u * 0.12; ctx.strokeRect(W * 0.1, H * 0.12, W * 0.18, H * 0.34); ctx.beginPath(); ctx.moveTo(W * 0.19, H * 0.12); ctx.lineTo(W * 0.19, H * 0.46); ctx.moveTo(W * 0.1, H * 0.29); ctx.lineTo(W * 0.28, H * 0.29); ctx.stroke();
      ctx.fillStyle = "#c0574c"; ctx.fillRect(W * 0.66, gy - u * 1.9, W * 0.26, u * 1.9); ctx.fillStyle = shade("#c0574c", -0.15); ctx.fillRect(W * 0.66, gy - u * 2.6, W * 0.26, u * 0.9);
      ctx.fillStyle = "#e9c46a"; ctx.fillRect(W * 0.44, H * 0.14, W * 0.1, H * 0.14); ctx.fillStyle = "#6aa84f"; ctx.fillRect(W * 0.45, H * 0.15, W * 0.08, H * 0.12);
    } else {
      ctx.fillStyle = "#2f5d46"; ctx.fillRect(W * 0.3, H * 0.1, W * 0.4, H * 0.3); ctx.strokeStyle = "#a9794a"; ctx.lineWidth = u * 0.2; ctx.strokeRect(W * 0.3, H * 0.1, W * 0.4, H * 0.3);
      ctx.strokeStyle = "rgba(255,255,255,0.8)"; ctx.lineWidth = u * 0.06; ctx.beginPath(); ctx.moveTo(W * 0.34, H * 0.2); ctx.lineTo(W * 0.5, H * 0.2); ctx.moveTo(W * 0.34, H * 0.27); ctx.lineTo(W * 0.58, H * 0.27); ctx.stroke();
      ctx.fillStyle = "#b98b5e"; [0.12, 0.84].forEach((x) => ctx.fillRect(W * x, gy - u * 1.2, W * 0.1, u * 0.2));
    }
    return;
  }
  const night = settingId === "night";
  if (night) { for (let i = 0; i < 60; i += 1) { const x = ((i * 7919) % 1000) / 1000 * W; const y = ((i * 6271) % 1000) / 1000 * gy * 0.8; ctx.fillStyle = `rgba(255,255,255,${0.5 + 0.5 * Math.sin(time * 2 + i)})`; ctx.fillRect(x, y, 2, 2); } rad(ctx, W * 0.82, H * 0.17, u * 0.9); ctx.fillStyle = "#f7f1c8"; ctx.fill(); }
  else { rad(ctx, W * 0.86, H * 0.16, u * 0.8); ctx.fillStyle = "#ffe27a"; ctx.fill(); cloud(ctx, W * (0.2 + ((time * 0.01) % 1) * 0.2), H * 0.17, u * 1.1); cloud(ctx, W * (0.62 - ((time * 0.008) % 1) * 0.15), H * 0.11, u * 0.9); }
  // far hills / mountains
  if (settingId === "mountain") { ctx.fillStyle = "#9fb1c4"; ctx.beginPath(); ctx.moveTo(0, gy); [[0.15, 0.32], [0.3, 0.55], [0.45, 0.3], [0.62, 0.6], [0.82, 0.35], [1, 0.52]].forEach(([x, h]) => ctx.lineTo(W * x, gy - H * h * 0.55)); ctx.lineTo(W, gy); ctx.fill(); ctx.fillStyle = "#fff"; [[0.3, 0.55], [0.62, 0.6]].forEach(([x, h]) => { ctx.beginPath(); ctx.moveTo(W * x - u * 0.8, gy - H * h * 0.55 + u * 1.1); ctx.lineTo(W * x, gy - H * h * 0.55); ctx.lineTo(W * x + u * 0.8, gy - H * h * 0.55 + u * 1.1); ctx.closePath(); ctx.fill(); }); }
  else { ctx.fillStyle = shade(s.ground, night ? 0.05 : 0.18); ctx.beginPath(); ctx.moveTo(0, gy); ctx.quadraticCurveTo(W * 0.25, gy - H * 0.14, W * 0.5, gy - H * 0.04); ctx.quadraticCurveTo(W * 0.75, gy - H * 0.17, W, gy - H * 0.05); ctx.lineTo(W, gy); ctx.fill(); }
  if (settingId === "beach") {
    ctx.fillStyle = "#4db6e8"; ctx.fillRect(0, gy - H * 0.14, W, H * 0.14);
    ctx.strokeStyle = "rgba(255,255,255,0.7)"; ctx.lineWidth = u * 0.08; for (let i = 0; i < 6; i += 1) { ctx.beginPath(); ctx.moveTo(((i * 0.2 + (time * 0.02) % 0.2) % 1.2) * W, gy - H * (0.03 + 0.02 * (i % 3))); ctx.lineTo(((i * 0.2 + (time * 0.02) % 0.2) % 1.2) * W + u * 1.2, gy - H * (0.03 + 0.02 * (i % 3))); ctx.stroke(); }
  }
  const ground = ctx.createLinearGradient(0, gy, 0, H); ground.addColorStop(0, s.ground); ground.addColorStop(1, shade(s.ground, -0.22)); ctx.fillStyle = ground; ctx.fillRect(0, gy, W, H - gy);
  if (settingId === "park" || settingId === "forest" || settingId === "mountain") { [0.08, 0.28, 0.9].forEach((x, i) => tree(ctx, W * x, gy + u * 0.2, u * (settingId === "forest" ? 3.2 : 2.5) - i * u * 0.1, settingId === "forest" ? "#2f7d4a" : "#4caf50")); }
  if (settingId === "forest") { [0.18, 0.5, 0.72, 0.96].forEach((x, i) => tree(ctx, W * x, gy - u * 0.1, u * 2.2 + (i % 2) * u * 0.4, "#3b8f55")); }
  if (settingId === "park") { ctx.fillStyle = "#e07a8f"; [0.4, 0.55, 0.7, 0.3].forEach((x, i) => { rad(ctx, W * x, H * (0.9 + 0.03 * (i % 2)), u * 0.14); ctx.fill(); }); }
  if (settingId === "village") { house(ctx, W * 0.15, gy, u * 2.6, "#f2c9a0", "#c0574c"); house(ctx, W * 0.82, gy, u * 3, "#c9dff2", "#6b4a8d"); tree(ctx, W * 0.5, gy - u * 0.2, u * 2, "#4caf50"); ctx.fillStyle = "#d9c9a0"; ctx.beginPath(); ctx.moveTo(W * 0.4, H); ctx.lineTo(W * 0.46, gy); ctx.lineTo(W * 0.54, gy); ctx.lineTo(W * 0.62, H); ctx.fill(); }
  if (settingId === "street") { [0.12, 0.45, 0.8].forEach((x, i) => { const w = W * 0.2; const h = H * (0.35 + 0.1 * i); ctx.fillStyle = ["#d98a6a", "#8fa8c7", "#c9b36a"][i]; ctx.fillRect(W * x - w / 2, gy - h, w, h); ctx.fillStyle = "#f6e7a8"; for (let r = 0; r < 3; r += 1) for (let c = 0; c < 3; c += 1) ctx.fillRect(W * x - w / 2 + w * (0.12 + c * 0.3), gy - h + h * (0.1 + r * 0.28), w * 0.18, h * 0.15); }); ctx.fillStyle = "#fff"; for (let i = 0; i < 6; i += 1) ctx.fillRect(W * (0.05 + i * 0.17), H * 0.93, W * 0.08, H * 0.012); }
  if (settingId === "farm") { ctx.fillStyle = "#c0392b"; ctx.fillRect(W * 0.7, gy - u * 2.6, u * 4, u * 2.6); ctx.fillStyle = "#8e2a20"; ctx.beginPath(); ctx.moveTo(W * 0.7 - u * 0.3, gy - u * 2.6); ctx.lineTo(W * 0.7 + u * 2, gy - u * 4.2); ctx.lineTo(W * 0.7 + u * 4.3, gy - u * 2.6); ctx.fill(); ctx.fillStyle = "#fff"; ctx.fillRect(W * 0.7 + u * 1.4, gy - u * 1.6, u * 1.2, u * 1.6); ctx.strokeStyle = "#b08850"; ctx.lineWidth = u * 0.1; for (let i = 0; i < 9; i += 1) { ctx.beginPath(); ctx.moveTo(W * (0.02 + i * 0.075), gy + u * 0.5); ctx.lineTo(W * (0.02 + i * 0.075), gy - u * 0.2); ctx.stroke(); } ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W * 0.62, gy); ctx.stroke(); }
}

// a small name label above the character who speaks
export function drawNameTag(ctx, text, x, y, size) {
  ctx.save();
  ctx.font = `700 ${size}px "Inter", "Segoe UI", Arial, sans-serif`;
  const w = ctx.measureText(text).width + size * 1.1;
  ctx.fillStyle = "rgba(10,20,40,0.78)";
  ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x - w / 2, y - size * 1.5, w, size * 1.7, size * 0.5) : ctx.rect(x - w / 2, y - size * 1.5, w, size * 1.7); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(text, x, y - size * 0.65);
  ctx.restore();
}
